import type { Campaign, CampaignDetail } from './types';

// Mint-Marketing › chi tiết chiến dịch (view=detail): số liệu phái sinh từ Campaign + CampaignDetail theo script DTV của board.
// Người nhận / phản hồi / giờ mở là dữ liệu minh hoạ như board (số thật chỉ có gửi / lỗi / bounce / trả lời / khách / Sale).

export type RecvKey = 'reply' | 'click' | 'open' | 'sent' | 'bounce' | 'optout' | 'fail' | 'pend';
export type RecvFilter = 'all' | 'open' | 'click' | 'reply' | 'bounce' | 'optout' | 'fail';

export const RECV_LABEL: Record<RecvKey, string> = { reply: 'Trả lời', click: 'Đã bấm', open: 'Đã mở', sent: 'Chưa mở', bounce: 'Bounce', optout: 'Huỷ ĐK', fail: 'Lỗi gửi', pend: 'Chờ gửi' };
export const RECV_COLOR: Record<RecvKey, string> = {
  reply: 'var(--d-green)', click: 'var(--d-amber)', open: 'var(--d-blue)', sent: 'var(--d-gray)', bounce: 'var(--err)', optout: 'var(--d-violet)', fail: 'var(--err)', pend: 'var(--d-gray)',
};

type Rr = readonly [sn: string, sale: string, addr: number, s: RecvKey, open: number, click: number, first: string];
const RR: readonly Rr[] = [
  ['ALIS INTERNATIONAL', 'ANDG', 2, 'reply', 3, 2, '26/Sep 13:05'], ['VJTrading', 'DAVID', 1, 'reply', 2, 1, '26/Sep 14:40'], ['THACO', 'TUANNT', 3, 'click', 4, 2, '26/Sep 12:51'],
  ['GREENBUD', 'DAVID', 2, 'click', 3, 1, '26/Sep 15:10'], ['SAVITA', 'DAVID', 2, 'open', 2, 0, '27/Sep 08:12'], ['NAMMY', 'ANDG', 1, 'open', 1, 0, '26/Sep 16:30'],
  ['TAMNGOC', 'DAVID', 1, 'open', 1, 0, '28/Sep 09:02'], ['NDP', 'TUANNT', 1, 'optout', 1, 0, '26/Sep 13:20'], ['TAINGUYEN', 'PHILIP', 2, 'bounce', 0, 0, ''],
  ['SNC', 'PHILIP', 2, 'bounce', 0, 0, ''], ['HAVI', 'PHILIP', 2, 'bounce', 0, 0, ''], ['THEKHANH', 'DAVID', 4, 'fail', 0, 0, ''], ['SEAPRO', 'DAVID', 1, 'sent', 0, 0, ''], ['SUNRISE', 'TUANNT', 1, 'sent', 0, 0, ''],
];
/** Cơ hội đã tạo sẵn từ chiến dịch (khoá `mã|khách`). */
const OPP0: Record<string, true> = { 'MKT-6UHT|VJTrading': true };

export interface Recipient { sn: string; sale: string; addr: number; s: RecvKey; open: number; click: number; first: string }
export interface Reply { sn: string; sale: string; when: string; k: string; canOpp: boolean; hasOpp: boolean }
export interface Hot { sn: string; open: number; click: number; opp: boolean }
export interface FunnelBar { k: string; n: number; w: number; p: string; c: string }

const pc = (a: number, b: number) => (b ? ((a / b) * 100).toFixed(1).replace('.', ',') : '0') + '%';
export const isSent = (c: Campaign) => c.status !== 'DRAFT' && c.status !== 'SCHEDULED' && c.sent > 0;

const REPLIES: Record<string, readonly (readonly [string, string, string, string])[]> = {
  'MKT-6UHT': [
    ['ALIS INTERNATIONAL', 'ANDG', '26/Sep 13:05', 'Hỏi giá kéo 40RF Bình Dương → Cát Lái, 4 chuyến / tuần'],
    ['ALIS INTERNATIONAL', 'ANDG', '27/Sep 09:20', 'Gửi thêm yêu cầu cắm điện trên đường'],
    ['VJTrading', 'DAVID', '26/Sep 14:40', 'Nhờ báo giá trucking lạnh đi Long An'],
  ],
};

export function recipients(c: Campaign, d: CampaignDetail, overrides: Record<string, boolean> = {}): Recipient[] {
  const sent = isSent(c);
  return RR.map(([sn, sale, addr, s0, open, click, first]) => {
    let s: RecvKey = s0;
    if (!sent) s = 'pend';
    else {
      if (s === 'reply' && !c.reply) s = 'click';
      if (s === 'bounce' && !d.bounce) s = 'sent';
      if (s === 'optout' && !d.optout) s = 'open';
      if (s === 'fail' && !c.fail) s = 'sent';
      if ((s === 'open' || s === 'click') && overrides[sn]) s = 'reply';
    }
    return { sn, sale, addr, s, open: sent ? open : 0, click: sent ? click : 0, first: sent && first ? first : '—' };
  });
}

export function filterTabs(c: Campaign, d: CampaignDetail, rows: readonly Recipient[], oppN: number): { key: RecvFilter; label: string; n: number; color: string; test: (r: Recipient) => boolean }[] {
  const replyN = replyCount(c, rows);
  void oppN;
  if (!isSent(c)) return [{ key: 'all', label: 'Chờ gửi', n: c.total || d.customers, color: RECV_COLOR.pend, test: () => true }];
  return [
    { key: 'all', label: 'Tất cả', n: c.total, color: RECV_COLOR.sent, test: () => true },
    { key: 'open', label: 'Mở', n: c.open, color: RECV_COLOR.open, test: (r) => r.s === 'open' || r.s === 'click' || r.s === 'reply' },
    { key: 'click', label: 'Bấm', n: c.click, color: RECV_COLOR.click, test: (r) => r.s === 'click' || r.s === 'reply' },
    { key: 'reply', label: 'Trả lời', n: replyN, color: RECV_COLOR.reply, test: (r) => r.s === 'reply' },
    { key: 'bounce', label: 'Bounce', n: d.bounce, color: RECV_COLOR.bounce, test: (r) => r.s === 'bounce' },
    { key: 'optout', label: 'Huỷ ĐK', n: d.optout, color: RECV_COLOR.optout, test: (r) => r.s === 'optout' },
    { key: 'fail', label: 'Lỗi', n: Math.max(0, c.fail - d.bounce), color: RECV_COLOR.fail, test: (r) => r.s === 'fail' },
  ];
}

/** Số trả lời = số thật + số khách sale đánh dấu thêm (bản mẫu: thư trả lời có sẵn trong RR không tính lại). */
export function replyCount(c: Campaign, rows: readonly Recipient[]): number {
  return c.reply + rows.filter((r) => r.s === 'reply' && !(c.reply && RR.some((q) => q[0] === r.sn && q[3] === 'reply'))).length;
}

export function oppCount(code: string, opp: Record<string, boolean>): number {
  return Object.keys(opp).filter((k) => opp[k] && k.startsWith(code + '|')).length;
}
export const oppSeed = (): Record<string, boolean> => ({ ...OPP0 });

export function funnel(c: Campaign, replyN: number, oppN: number): FunnelBar[] {
  const rows: readonly (readonly [string, number, string])[] = [
    ['Đã gửi', c.sent, 'var(--d-gray)'], ['Mở', c.open, 'var(--d-blue)'], ['Bấm', c.click, 'var(--d-amber)'], ['Trả lời', replyN, 'var(--d-green)'], ['Thành cơ hội', oppN, 'var(--d-violet)'],
  ];
  const max = Math.max(1, c.sent);
  return rows.map(([k, n, col], i) => ({ k, n, w: n ? Math.max(2, Math.round((n / max) * 100)) : 0, p: i ? pc(n, c.sent) : '100%', c: col }));
}

const SO = [0, 0.34, 0.54, 0.69, 0.79, 0.86, 0.93, 0.98, 1];
const SC = [0, 0.3, 0.52, 0.68, 0.79, 0.87, 0.94, 0.99, 1];
export const TL_X = ['+0h', '+1h', '+2h', '+4h', '+8h', '+12h', '+24h', '+48h', '+72h'] as const;
export const TL = { W: 600, H: 210, x0: 44, x1: 16, top: 18, bot: 30 } as const;
export const fmtPct = (v: number) => v.toFixed(1).replace('.', ',') + '%';

function linePath(h: readonly number[], max: number) {
  const x0 = TL.x0, x1 = TL.W - TL.x1, yb = TL.H - TL.bot;
  const pts = h.map((v, i) => [x0 + (i * (x1 - x0)) / Math.max(1, h.length - 1), yb - (v / max) * (yb - TL.top)] as const);
  const d = pts.map((q, i) => (i ? 'L' : 'M') + q[0].toFixed(1) + ' ' + q[1].toFixed(1)).join(' ');
  const last = pts[pts.length - 1]!;
  return { d, area: `${d} L${last[0].toFixed(1)} ${yb} L${pts[0]![0].toFixed(1)} ${yb} Z`, pts, lx: last[0], ly: last[1] };
}

export function timeline(c: Campaign) {
  const oP = c.sent ? (c.open / c.sent) * 100 : 0;
  const cP = c.sent ? (c.click / c.sent) * 100 : 0;
  const max = Math.max(10, Math.ceil(oP / 10) * 10 + 10);
  const yb = TL.H - TL.bot;
  const open = linePath(SO.map((v) => +(v * oP).toFixed(1)), max);
  const click = linePath(SC.map((v) => +(v * cP).toFixed(1)), max);
  const grid = [0, max / 2, max].map((v) => { const y = yb - (v / max) * (yb - TL.top); return { y, ty: y + 4, k: v + '%' }; });
  return { oP, cP, open, click, grid };
}

/** Giờ mở trong ngày (minh hoạ: % so với giờ đông nhất). */
export const HOURS: readonly (readonly [string, number])[] = [['7', 20], ['8', 62], ['9', 80], ['10', 55], ['11', 34], ['12', 12], ['13', 18], ['14', 41], ['15', 38], ['16', 26], ['17', 14], ['18', 6]];

export function hotRows(rows: readonly Recipient[], code: string, opp: Record<string, boolean>): Hot[] {
  return rows.filter((r) => r.s === 'click' || (r.s === 'open' && r.open >= 2)).map((r) => ({ sn: r.sn, open: r.open, click: r.click, opp: !!opp[code + '|' + r.sn] }));
}

export function replies(c: Campaign, rows: readonly Recipient[], opp: Record<string, boolean>): Reply[] {
  const base = REPLIES[c.id] ?? [];
  const extra = rows.filter((r) => r.s === 'reply' && !base.some((q) => q[0] === r.sn)).map((r) => [r.sn, r.sale, '—', 'Sale đánh dấu đã trả lời'] as const);
  const seen = new Set<string>();
  return [...base, ...extra].map(([sn, sale, when, k]) => {
    const first = !seen.has(sn);
    seen.add(sn);
    const has = !!opp[c.id + '|' + sn];
    return { sn, sale, when, k, canOpp: first && !has, hasOpp: first && has };
  });
}

export interface AbRow { k: string; subj: string; win: boolean; rate: number }
const AB: Record<string, readonly (readonly [string, string, number, number])[]> = {
  'MKT-6UHT': [['A', 'Giải pháp trucking lạnh chuyên nghiệp gửi «ContactName»', 247, 108], ['B', 'Trucking lạnh 24/7 · Bình Dương → Cát Lái', 247, 93]],
};
export function abRows(c: Campaign): { ab: boolean; rows: AbRow[] } {
  const ab = AB[c.id];
  if (!ab) return { ab: false, rows: [{ k: 'A', subj: c.subject, win: false, rate: c.sent ? Math.round((c.open / c.sent) * 100) : 0 }] };
  return {
    ab: true,
    rows: ab.map(([k, subj, n, o], i) => { const other = ab[1 - i]!; return { k, subj, win: o / n > other[3] / other[2], rate: Math.round((o / n) * 100) }; }),
  };
}

export function history(c: Campaign, d: CampaignDetail, oppN: number): { when: string; k: string; c: string }[] {
  const rp = REPLIES[c.id] ?? [];
  if (isSent(c)) {
    return [
      { when: d.made, k: 'Tạo nháp · ' + c.owner, c: 'var(--d-gray)' },
      { when: d.start, k: `Bắt đầu gửi ${c.total} địa chỉ`, c: 'var(--d-blue)' },
      { when: d.end, k: `Gửi xong · ${c.sent} thành công`, c: 'var(--d-green)' },
      ...(d.bounce ? [{ when: d.end, k: `Quét thư dội · ${d.bounce} địa chỉ hỏng`, c: 'var(--err)' }] : []),
      ...(rp.length ? [{ when: rp[0]![2], k: rp[0]![0] + ' trả lời', c: 'var(--d-green)' }] : []),
      ...(oppN ? [{ when: '27/Sep 10:15', k: oppN + ' cơ hội từ chiến dịch', c: 'var(--d-violet)' }] : []),
    ];
  }
  return [{ when: d.made, k: 'Tạo nháp · ' + c.owner, c: 'var(--d-gray)' }, ...(c.status === 'SCHEDULED' ? [{ when: d.start, k: 'Hẹn gửi', c: 'var(--d-blue)' }] : [])];
}

export function infoRows(c: Campaign, d: CampaignDetail, statusLabel: string): [string, string][] {
  return [
    ['Mã chiến dịch', c.id], ['Trạng thái', statusLabel], ['Mẫu thư', c.template], ['Người tạo', c.owner], ['Tạo lúc', d.made],
    [c.status === 'SCHEDULED' ? 'Hẹn gửi' : 'Bắt đầu gửi', d.start], ['Gửi xong', d.end], ['Hộp gửi', d.sender], ['Cách gửi', 'Từng địa chỉ'],
    ['Theo dõi', 'Pixel mở · link gắn mã'], ['Từ chối nhận', 'Link chân mail'],
  ];
}
